import {
    Box,
    Button,
    Checkbox,
    Divider,
    FormControlLabel,
    FormGroup,
    IconButton,
    Link,
    Menu,
    OutlinedInput,
    Typography,
} from '@mui/material';
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { i18n } from '../../../../i18n/i18n';
import { Filter, FilterOption, FiltersHandlers } from '../../filter-types';
import { COLORS } from '../../../../constants/constants';
import { ReactComponent as ArrowUp } from '../../../../images/arrow-up.svg';
import { ReactComponent as ArrowDown } from '../../../../images/arrow-down.svg';
import { ReactComponent as MagnifyingGlassIcon } from '../../../../images/search.svg';
import { ReactComponent as CheckboxSelectedIcon } from '../../../../images/checkbox-selected.svg';
import { ReactComponent as CheckboxUnselected } from '../../../../images/checkbox-unselected.svg';
import { usePopup } from '@cellebrite/saas-ui-common';
import _ from 'lodash';

interface MultiSelectionFilterProps {
    filter: Filter;
    onApply: (filterName: string, selectedValues: FilterOption[]) => void;
}

const MultiSelectionFilter = forwardRef<FiltersHandlers, MultiSelectionFilterProps>(
    ({ filter, onApply }, forwaredRef) => {
        const [filteredOptions, setFilteredOptions] = useState<FilterOption[]>([]);
        const [searchInput, setSearchInput] = useState('');
        const [selectedOptions, setSelectedOptions] = useState<FilterOption[]>([]);
        const [chosenOptions, setChosenOptions] = useState<FilterOption[]>([]);
        const [open, anchorEl, handleClick, handleClose] = usePopup(null, () =>
            setTimeout(() => {
                setSearchInput('');
                setSelectedOptions([]);
            }, 300),
        );

        useEffect(() => {
            setFilteredOptions(
                searchInput.length
                    ? filter.options.filter((opt) => opt.label.toLowerCase().includes(searchInput.toLowerCase()))
                    : filter.options,
            );
        }, [filter.options, searchInput]);

        useImperativeHandle(forwaredRef, () => ({
            updateChosenSelection: (options) => setChosenOptions(options as FilterOption[]),
            resetChosenSelection: () => setChosenOptions([]),
            removeChosenSelectionByValue: (value) =>
                setChosenOptions((prev) => prev.filter((option) => option.value !== value)),
        }));
        const filterName = (
            <Typography
                className="color-bd"
                sx={{
                    fontSize: '16px',
                    fontWeight: '500',
                    whiteSpace: 'nowrap',
                }}
            >
                {filter.name}
            </Typography>
        );
        return (
            <>
                <Button
                    color="tetriary"
                    sx={{
                        backgroundColor: chosenOptions.length || open ? COLORS.BRAND_LIGHTEST : null,
                    }}
                    onClick={(e) => {
                        setSelectedOptions(chosenOptions);
                        handleClick(e);
                        if (!open) {
                            setSelectedOptions(filter.options.filter((opt) => opt.initialChecked));
                        }
                    }}
                >
                    <Box
                        sx={{
                            mr: '14px',
                        }}
                    >
                        {chosenOptions.length ? (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {filterName}
                                <Typography
                                    className="color-b"
                                    sx={{
                                        ml: chosenOptions.length === 1 ? 1 : 0.5,
                                        fontSize: '16px',
                                        fontWeight: '500',
                                        whiteSpace: 'nowrap',
                                    }}
                                >{`${chosenOptions.length === 1 ? chosenOptions[0].label : '(' + chosenOptions.length + ')'}`}</Typography>
                            </Box>
                        ) : (
                            filterName
                        )}
                    </Box>
                    {open ? (
                        <ArrowUp className="fill-b" />
                    ) : (
                        <ArrowDown className={chosenOptions.length ? 'fill-b' : 'fill-bd'} />
                    )}
                </Button>

                <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                    <Box sx={{ p: 1, minWidth: 256 }}>
                        {filter.searchableOptions && (
                            <OutlinedInput
                                sx={{
                                    'height': '40px',
                                    'borderRadius': '8px',
                                    '&:hover fieldset': {
                                        border: `1px solid ${COLORS.BRAND_HOVER} !important`,
                                    },
                                    'width': '100%',
                                }}
                                autoFocus
                                placeholder={i18n.translate('Find...')}
                                type="text"
                                size="small"
                                value={searchInput}
                                onChange={(event) => setSearchInput(event.target.value)}
                                startAdornment={
                                    <IconButton edge="start" className="fill-md">
                                        <MagnifyingGlassIcon />
                                    </IconButton>
                                }
                            />
                        )}

                        <FormGroup>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    pt: filter.searchableOptions ? 1 : 0,
                                    pb: 1,
                                }}
                            >
                                <FormControlLabel
                                    sx={{ m: 0 }}
                                    key="Select all"
                                    control={
                                        <Checkbox
                                            checkedIcon={<CheckboxSelectedIcon />}
                                            icon={<CheckboxUnselected />}
                                            checked={selectedOptions.length === filter.options.length}
                                            onChange={() =>
                                                setSelectedOptions(
                                                    selectedOptions.length === filter.options.length
                                                        ? []
                                                        : filter.options,
                                                )
                                            }
                                        />
                                    }
                                    label={i18n.translate('Select all')}
                                />
                                <Link onClick={() => setSelectedOptions([])}>{i18n.translate('Clear all')}</Link>
                            </Box>
                            <Divider sx={{ ml: '-8px', mr: '-8px', mb: 1 }} />

                            <Box sx={{ maxHeight: 200, flexWrap: 'unset', overflow: 'scroll', pl: 3 }}>
                                {filteredOptions.map((option, index) => (
                                    <Box
                                        key={index}
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <FormControlLabel
                                            key={index}
                                            control={
                                                <Checkbox
                                                    checkedIcon={<CheckboxSelectedIcon />}
                                                    icon={<CheckboxUnselected />}
                                                    sx={{
                                                        height: '32px',
                                                        mx: 0,
                                                    }}
                                                    checked={
                                                        !!selectedOptions.find((opt) =>
                                                            _.isEqual(opt.value, option.value),
                                                        )
                                                    }
                                                    onChange={() =>
                                                        setSelectedOptions(
                                                            selectedOptions.find((opt) =>
                                                                _.isEqual(opt.value, option.value),
                                                            )
                                                                ? selectedOptions.filter(
                                                                      (opt) => !_.isEqual(opt.value, option.value),
                                                                  )
                                                                : [...selectedOptions, option],
                                                        )
                                                    }
                                                />
                                            }
                                            label={
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    {option.labelPrefix}
                                                    <Typography>{option.label}</Typography>
                                                </Box>
                                            }
                                        />{' '}
                                        {option.count && <Typography>{option.count}</Typography>}
                                    </Box>
                                ))}
                            </Box>
                        </FormGroup>

                        <Box sx={{ float: 'right', pb: 1, pr: 1, pt: 3 }} className="background-color-l">
                            <Button sx={{ height: 32 }} color="secondary" onClick={() => handleClose()}>
                                {i18n.translate('Cancel')}
                            </Button>
                            <Button
                                sx={{ height: 32, ml: 1 }}
                                onClick={() => {
                                    onApply(filter.originalName, selectedOptions);
                                    setChosenOptions(selectedOptions);
                                    handleClose();
                                }}
                            >
                                {i18n.translate('Apply')}
                            </Button>
                        </Box>
                    </Box>
                </Menu>
            </>
        );
    },
);

export default MultiSelectionFilter;
